// Broker Channel Batch (WS6) — "Assign to team" graduation modal, opened
// from the Certified-column "Ready to graduate" affordance on the kanban.
// Picks an Open broker team and POSTs the membership (the backend moves a
// broker already on another team in the same action).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BrokerSummaryDto } from "@/api/brokers";
import { brokerTeamsApi } from "@/api/brokerTeams";
import { extractErrorMessage } from "@/api/client";
import { brokerKeys, brokerTeamKeys } from "@/hooks/brokers/brokerKeys";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Button, useToast } from "@/design-system/components/ui";

export function AssignToTeamModal({
  broker,
  onClose,
}: {
  // Only id + companyName are read, so the broker DETAIL page can pass its
  // BrokerDetailDto too (WS6 detail-page graduation affordance).
  broker: Pick<BrokerSummaryDto, "id" | "companyName">;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [teamId, setTeamId] = useState("");

  const teams = useQuery({
    queryKey: brokerTeamKeys.list(false),
    queryFn: () => brokerTeamsApi.list(false),
  });
  const openTeams = (teams.data ?? []).filter((t) => t.status === "Open");

  const assign = useMutation({
    mutationFn: () => brokerTeamsApi.assignMember(teamId, broker.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: brokerKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: brokerTeamKeys.all });
      // WS6 — the detail page reads teamId/teamName/readyToGraduate off the
      // broker detail query; refresh it so the header chips update in place.
      void queryClient.invalidateQueries({ queryKey: brokerKeys.detail(broker.id) });
      const team = openTeams.find((t) => t.id === teamId);
      toast.success(
        `${broker.companyName} graduated to ${team ? `"${team.name}"` : "the team"}.`,
      );
      onClose();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Assign to team"
      description={`Graduate ${broker.companyName} onto a broker team.`}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={assign.isPending}
            disabled={!teamId || assign.isPending}
            onClick={() => assign.mutate()}
            data-testid="assign-to-team-confirm"
          >
            Assign
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-1" data-testid="assign-to-team-modal">
        <span className="text-caption text-gray-mid uppercase tracking-label">Team</span>
        <Select
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          disabled={teams.isLoading}
          aria-label="Team"
        >
          <option value="">{teams.isLoading ? "Loading…" : "Select team…"}</option>
          {openTeams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </Select>
        {!teams.isLoading && openTeams.length === 0 && (
          <p className="text-small text-gray-mid m-0">
            No open teams yet — create one under Partners → Teams.
          </p>
        )}
      </div>
    </Modal>
  );
}
